// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//
// If you have dependencies that try to import CSS, esbuild will generate a separate `app.css` file.
// To load it, simply add a second `<link>` to your `root.html.heex` file.

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html"
// Establish Phoenix Socket and LiveView configuration.
import {Socket} from "phoenix"
import {LiveSocket} from "phoenix_live_view"
import {hooks as colocatedHooks} from "phoenix-colocated/tama"
import {PasskeyRegister, PasskeyAuthenticate} from "./webauthn"
import {AvatarCrop} from "./avatar_crop"
import {PushToggle} from "./push"
import topbar from "../vendor/topbar"

// GlitchTip/Sentry-Frontend-Fehlererfassung. DSN, Release und Environment
// rendert der Server als Meta-Tags (siehe root.html.heex) aus ENV. Ohne DSN
// (Dev/Test oder fehlendes Secret) bleibt alles no-op.
//
// Sentry selbst wird erst nachgeladen, wenn ein Fehler auftritt — 28 KB gzip,
// die sonst jeder Besuch zahlt, obwohl die meisten Sessions nie einen Fehler
// sehen. Damit der erste (interessanteste) Fehler nicht verlorengeht, faengt
// ein eigener Listener ihn schon vor dem Nachladen ab und spielt ihn danach
// in Sentry nach.
const sentryMeta = name =>
  document.querySelector(`meta[name='${name}']`)?.getAttribute("content")
const sentryDsn = sentryMeta("sentry-dsn")
if (sentryDsn) {
  const buffered = []

  const handleError = event => {
    buffered.push(event)
    if (buffered.length > 1) return // Ladevorgang laeuft schon
    loadSentry()
  }

  const loadSentry = async () => {
    try {
      const {init, captureException} = await import("./sentry_lazy.js")
      init({
        dsn: sentryDsn,
        release: sentryMeta("sentry-release") || undefined,
        environment: sentryMeta("sentry-environment") || undefined,
        // Same-Origin-Tunnel: Envelopes gehen an /intel statt direkt an GlitchTip,
        // damit Adblocker die GlitchTip-Domain nicht blocken. Server leitet weiter.
        tunnel: "/intel",
      })
      buffered.forEach(e => captureException(e.error ?? e.reason ?? e))
    } catch {
      // Fehlererfassung, die die App crasht, waere absurd.
    } finally {
      window.removeEventListener("error", handleError)
      window.removeEventListener("unhandledrejection", handleError)
    }
  }

  window.addEventListener("error", handleError)
  window.addEventListener("unhandledrejection", handleError)
}

// Service Worker: Offline-Fallback + Cache der eigenen Assets (#169). Eine
// Registrierung fuer die ganze App, unabhaengig davon, ob Web-Push (push.js)
// je angeschaltet wird — die braucht denselben Worker fuer denselben Zweck und
// wartet dort nur noch auf ihn (`serviceWorker.ready`), statt selbst nochmal
// zu registrieren.
if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js")

// Wo sitzt der Mensch? Nur der Browser weiß es (RFC 0004): daraus wird die Zone
// vorbelegt, in der ein neues Pet leben soll. Eine Behauptung, kein Beweis — der
// Server prüft sie gegen die IANA-Datenbank (Pet.valid_timezone?/1).
const detectedTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return null
  }
}

// Die Auswahlliste kommt auch aus dem Browser: die IANA-Kennungen stehen dort
// schon (Intl.supportedValuesOf), und sie dafür aus einer zweiten Elixir-Abhängigkeit
// zu ziehen wäre eine Liste von Strings zum Preis eines Deploys. `phx-update="ignore"`,
// weil LiveView die Optionen sonst bei jedem Diff wieder wegräumt.
const TimezonePicker = {
  mounted() {
    const zones = Intl.supportedValuesOf?.("timeZone") || []
    const current = this.el.dataset.timezone

    // Kennt der Browser die Liste nicht (älteres Safari), bleibt wenigstens die
    // gesetzte Zone wählbar — dann ist das Feld eben nur eine Anzeige.
    for (const zone of zones.length ? zones : [current].filter(Boolean)) {
      const option = document.createElement("option")
      option.value = zone
      option.textContent = zone.replace(/_/g, " ")
      option.selected = zone === current
      this.el.appendChild(option)
    }
  },
}

// ─── Der Weg von der Liste zum Tier (View Transitions) ──────────────────────
// Ein Besuch dauert unter einer Minute und beginnt fast immer in der Liste. Ohne
// Uebergang ist das ein Seitenwechsel: die Zeile ist weg, die Buehne ist da, und
// dazwischen liegt ein Schnitt. Mit Uebergang ist es *dieselbe Figur*, die aus
// ihrer Zeile auf die Buehne waechst — aus zwei Screens wird ein Moment.
//
// LiveView patcht das Dokument selbst; `onDocumentPatch` reicht uns den Patch,
// damit wir ihn in `document.startViewTransition` wickeln koennen. Nur duerfen
// wir das nicht bei *jedem* Patch tun: das Pet tickt alle fuenf Minuten, und eine
// Seite, die alle fuenf Minuten ueberblendet, flackert. Deshalb wird der naechste
// Patch scharf gestellt (`armed`), und das tut genau eines: ein Klick auf einen
// Live-Navigations-Link. (Die Menue-Knoepfe taten es einmal per `data-vt` — dort
// wanderte aber nichts, es blieb ein Geist des Knopfes ueber dem Menue.)
//
// `view-transition-name` ist pro Dokument einmalig. Die Figur bekommt ihn also
// nicht im Markup, sondern erst zum Klick — einmal an der Quelle (die Zeile),
// und drinnen im Callback noch einmal am Ziel (die Buehne), das es vor dem Patch
// noch gar nicht gab. Danach fliegt er wieder weg.
const FIGURE_NAME = "pet-figure"
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)")
const canTransition = () => !!document.startViewTransition && !reducedMotion.matches

let armedPatch = false
let armedTimer = null
let morphPetId = null

const disarm = () => {
  armedPatch = false
  clearTimeout(armedTimer)
}

// Ein Klick, dem kein Patch folgt (externer Link, abgebrochene Navigation), darf
// die Scharfstellung nicht offen liegen lassen — sonst erwischt sie den naechsten
// Tick.
const arm = () => {
  armedPatch = true
  clearTimeout(armedTimer)
  armedTimer = setTimeout(() => (armedPatch = false), 1500)
}

const petIdForLink = link => {
  const href = link.getAttribute("href") || ""
  const match = href.match(/^\/pets\/(\d+)$/)
  if (match) return match[1]
  // Zurueck in die Liste: die Figur, die gerade auf der Buehne steht, ist die,
  // die in ihre Zeile zurueckschrumpft.
  if (href === "/pets") return document.querySelector("[data-pet-figure]")?.dataset.petFigure
  return null
}

const nameFigure = id => {
  if (!id) return
  const el = document.querySelector(`[data-pet-figure="${CSS.escape(id)}"]`)
  if (el) el.style.viewTransitionName = FIGURE_NAME
}

const unnameFigures = () => {
  document
    .querySelectorAll("[data-pet-figure]")
    .forEach(el => el.style.removeProperty("view-transition-name"))
}

// Capture-Phase: LiveView haengt sich an denselben Klick und faengt an zu
// navigieren, bevor ein Bubbling-Listener drankaeme.
document.addEventListener(
  "click",
  event => {
    if (!canTransition()) return

    const link = event.target.closest("a[data-phx-link]")
    if (!link) return

    morphPetId = petIdForLink(link)
    nameFigure(morphPetId)
    arm()
  },
  true,
)

const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content")
const liveSocket = new LiveSocket("/live", Socket, {
  longPollFallbackMs: 2500,
  params: {_csrf_token: csrfToken, timezone: detectedTimezone()},
  hooks: {
    ...colocatedHooks,
    PasskeyRegister,
    PasskeyAuthenticate,
    TimezonePicker,
    AvatarCrop,
    PushToggle,
  },
  dom: {
    onDocumentPatch(start) {
      if (!armedPatch || !canTransition()) return start()

      disarm()
      const id = morphPetId
      morphPetId = null

      // `start()` so frueh wie moeglich (LiveView-Doku): der Callback ist der
      // Moment, in dem der Browser den *neuen* Zustand aufnimmt — was danach
      // kommt, ist nur noch das Benennen der Zielfigur.
      //
      // Der `try` ist kein Zierrat: wer `start()` nicht aufruft, haengt die Seite
      // auf (so steht es in der LiveView-Doku). Wirft `startViewTransition`
      // synchron, ist der Callback noch nicht gelaufen — dann patchen wir eben
      // ohne Uebergang. Eine Animation ist es nicht wert, ein Tier unerreichbar
      // zu machen.
      try {
        const transition = document.startViewTransition(() => {
          start()
          nameFigure(id)
        })

        transition.finished.catch(() => {}).finally(unnameFigures)
      } catch (_error) {
        unnameFigures()
        start()
      }
    },
  },
})

// Show progress bar on live navigation and form submits.
// topbar paints into a canvas, so it needs a plain sRGB string, not the oklch token.
const topbarColor = () =>
  getComputedStyle(document.documentElement).getPropertyValue("--topbar-color").trim() || "#29d"

const configureTopbar = () =>
  topbar.config({barColors: {0: topbarColor()}, shadowColor: "rgba(0, 0, 0, .3)"})

configureTopbar()
window.addEventListener("phx:set-theme", () => requestAnimationFrame(configureTopbar))
window.addEventListener("phx:page-loading-start", _info => topbar.show(300))
window.addEventListener("phx:page-loading-stop", _info => topbar.hide())

// Sprachwahl: Cookie setzen (Plug + on_mount lesen es) und neu laden.
window.addEventListener("phx:set-locale", (e) => {
  const locale = e.target.dataset.locale
  document.cookie = `locale=${locale};path=/;max-age=31536000;samesite=lax`
  window.location.reload()
})

// connect if there are any LiveViews on the page
liveSocket.connect()

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket

// The lines below enable quality of life phoenix_live_reload
// development features:
//
//     1. stream server logs to the browser console
//     2. click on elements to jump to their definitions in your code editor
//
if (process.env.NODE_ENV === "development") {
  window.addEventListener("phx:live_reload:attached", ({detail: reloader}) => {
    // Enable server log streaming to client.
    // Disable with reloader.disableServerLogs()
    reloader.enableServerLogs()

    // Open configured PLUG_EDITOR at file:line of the clicked element's HEEx component
    //
    //   * click with "c" key pressed to open at caller location
    //   * click with "d" key pressed to open at function component definition location
    let keyDown
    window.addEventListener("keydown", e => keyDown = e.key)
    window.addEventListener("keyup", _e => keyDown = null)
    window.addEventListener("click", e => {
      if(keyDown === "c"){
        e.preventDefault()
        e.stopImmediatePropagation()
        reloader.openEditorAtCaller(e.target)
      } else if(keyDown === "d"){
        e.preventDefault()
        e.stopImmediatePropagation()
        reloader.openEditorAtDef(e.target)
      }
    }, true)

    window.liveReloader = reloader
  })
}

